import AddIcon from '@mui/icons-material/Add'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ErrorIcon from '@mui/icons-material/Error'
import InfoIcon from '@mui/icons-material/Info'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Checkbox,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Button as MuiButton,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Stack,
  Divider,
  MenuItem,
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)
import {
  default as React,
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import { isValidPhoneNumber } from 'react-phone-number-input'
import { AsyncPaginate } from 'react-select-async-paginate'
import { toast } from 'react-toastify'
import {
  AvailableScreenerComponent,
  CMBCBootstrapDialog,
  CMBCBootStrapDialogPaperComponent,
  getLabelForAlreadyPatientOption,
  giveTodayYesterdayElseFormattedStringPerLocal,
  handleUpdatePatientDetails,
  identifyStringUseForAutoFill,
  isWithinLast24Hours,
  loadOptionsForExistingPatient,
  makeBookingInDayViewCalendar,
  PatientFormComponent,
  PatientSearchCustomOption,
  PatientSearchPlaceholder,
  VitalsComponentInsideNewBooking,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { createNewPatient } from 'src/clinic/utils/patientUtils'
import { getCorrectScreeningReasonMessageObject } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsHelper'
import {
  cmbcCustomNoOptionsMessageComponentFunction,
  cmbcCustomResetClearFuncComponent,
} from 'src/clinic/components/ClinicManageBookingsPage/clinicBookingUtils.jsx'
import {
  EDVCBookNewSlotSelect,
  EDVCNewBookingContainer,
  PIParent,
  PISectionInput,
  SearchSectionParent,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingsPageStyles'
import {
  buildPatientDataForSelectingForNewBookingCalendar,
  buildRelatedPatientData,
  isPatientEmailValid,
  isPatientMobileValid,
  isValidPatientInfoForCreation,
} from 'src/clinic/components/ClinicManageBookingsPage/newBookingCalendarUtils'
import EventSelectorWithVisitEventTypesSelection from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/eventSelector/EventSelectorWithVisitEventTypesSelection'
import SelectedEventDataViewer from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/eventSelector/SelectedEventDataViewer'
import NewBookingDialogReasonForNotDoingScreeningForm from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialog/NewBookingDialogReasonForNotDoingScreeningForm'
import BookingDialogActions from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialogComponent/components/BookingDialogActions'
import EditPatientForm from 'src/clinic/components/ClinicManageBookingsPage/newBookingDialogComponent/components/EditPatientForm'
import { PastBookingsHorizontalComponent } from 'src/clinic/components/ClinicManageBookingsPage/PastBookingsHorizonal'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { attemptToBindLatestTodayVitalToThisBooking } from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import {
  allAvailableEventTypes,
  BLANK_PATIENT_DATA,
  currency_list,
  displayNamesOfEventTypes,
  GENDER_LIST_OFFLINE,
  OFFLINE_PAYMENT_METHODS_LIST,
  PATIENT_VALIDATION_FIELDS,
} from 'src/consumer/constants'
import {
  areStringsEqualIgnoreCase,
  removePlusFromBeginning,
  validateEmail,
  getExactTimeDiff,
} from 'src/consumer/helpers/utilFunctions'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { GenericDisplayFlex } from 'src/mentor/layout/Styles'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { postCPDataWithoutParams } from 'src/clinic/services'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import {
  buildPatientFormI18n,
  getSupportedPatientFormUiLanguage,
} from 'src/clinic/components/ClinicManageBookingsPage/patientForm/utils/patientFormI18n'
import {
  formatPatientAgeSummary,
  hasValidPatientAgeInput,
} from 'src/clinic/components/ClinicManageBookingsPage/patientForm/utils/patientAgeUtils'

// Forward ref wrapper for AsyncPaginate to fix ref warning
// AsyncPaginate doesn't support refs directly, so we use useImperativeHandle
// to expose a focus method that accesses the underlying input element via DOM
const AsyncPaginateWithRef = forwardRef((props, ref) => {
  const containerRef = useRef(null)
  const inputId = props.inputId || `async-paginate-input-${Math.random().toString(36).substr(2, 9)}`

  useImperativeHandle(ref, () => ({
    focus: () => {
      // Use setTimeout to ensure DOM is rendered
      setTimeout(() => {
        // Try multiple methods to find the input element
        const inputElement = 
          document.getElementById(inputId) || 
          containerRef.current?.querySelector?.('input[type="text"]') ||
          containerRef.current?.querySelector?.('input') ||
          document.querySelector(`input[aria-controls*="react-select"]`)
        
        if (inputElement) {
          inputElement.focus()
        }
      }, 0)
    },
    blur: () => {
      const inputElement = 
        document.getElementById(inputId) || 
        containerRef.current?.querySelector?.('input[type="text"]') ||
        containerRef.current?.querySelector?.('input')
      
      if (inputElement) {
        inputElement.blur()
      }
    }
  }))

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <AsyncPaginate {...props} inputId={inputId} />
    </div>
  )
})
AsyncPaginateWithRef.displayName = 'AsyncPaginateWithRef'

export default function NewBookingDialogComponent({
  dialogTitle,
  dialogSecondaryTitle,
  open,
  handleCloseDialog,
  setNewBookingDialogOpen,
  clinicId,
  clinicBrandId,
  clinicData,
  clinicBrandData,
  bookedSlotsInDay,
  meetingBookApiSignature,
  patientSearchApiSignature = '/api/secure/mentor/patient/list',
  pastPatientBookingsApiSignature,
  updateBookingsInCalendar,
  forMentor,
  mentorProfileData,
  mentorAuthorisedToSaveAndGetPatientsUsingIdb,
  chambersData,
  assistantProfileInThisClinic,
  selectedEventTypeId,
  eventDataForSelectedEventType,
  planDataForSelectedEventId,
  eventTypesVisible,
  setSelectedEventTypeId,
  clickedWantedSlotData,
  setClickedWantedSlotData,
  setEventDataForReceiptPrinting,
  setEventIdForReceiptPrinting,
  openReceiptDialog,
  selectedVisitEventType,
  setSelectedVisitEventType,
  createPatientApiSignature,
  patientUpdateApiSignature = '/api/secure/patient/update',
  paymentLinkToSendViaQRCode,
  bookingConsultationFor = null,
  isInDialog = false,
  selectedPatientOverride = null,
  forceAnonymousPatient = false,
  importedFromItems = [],
  forceHoldingSlot = false,
  hideBookButton = false,
  vitalsFormGetApiSignature,
}) {
  console.log({ hideBookButton })
  const clinicContext = useContext(ClinicDataContext)
  const isClinicAssistantPortal =
    typeof window !== 'undefined' && window.location.pathname.startsWith('/ca-home')
  const clinicAssistantLanguagePreference =
    clinicContext?.assistantData?.clinicAssistantSelfProfile?.language
  const clinicDefaultLanguage =
    clinicBrandData?.patientFormSettings?.defaultCommunicationLanguage ||
    clinicBrandData?.defaultCommunicationLanguage ||
    'en'
  const preferredPatientFormUiLanguage = isClinicAssistantPortal
    ? clinicAssistantLanguagePreference || clinicDefaultLanguage || 'en'
    : 'en'
  const shouldShowPatientFormUiLanguageSelector =
    isClinicAssistantPortal &&
    [clinicAssistantLanguagePreference, clinicDefaultLanguage].some(
      (languageCode) =>
        Boolean(languageCode) && languageCode.toLowerCase().split('-')[0] !== 'en',
    )
  const [patientFormUiLanguage, setPatientFormUiLanguage] = useState(
    getSupportedPatientFormUiLanguage(preferredPatientFormUiLanguage),
  )
  const patientFormI18n = useMemo(
    () => buildPatientFormI18n(patientFormUiLanguage),
    [patientFormUiLanguage],
  )
  const patientFormCopy = patientFormI18n.copy

  useEffect(() => {
    if (open) {
      setPatientFormUiLanguage(getSupportedPatientFormUiLanguage(preferredPatientFormUiLanguage))
    }
  }, [open, preferredPatientFormUiLanguage])

  // states:
  const [isNewPatient, setIsNewPatient] = useState(false)
  const [relatedToPatient, setRelatedToPatient] = useState()
  const [relatedPatientLoading, setRelatedPatientLoading] = useState(false)
  const [selectedPat, setSelectedPatient] = useState(null)
  const selectedPatient = selectedPatientOverride || selectedPat

  const [selectedPatientInputDataValidations, setSelectedPatientInputDataValidations] =
    useState(PATIENT_VALIDATION_FIELDS)
  const [selectedPatientChangesDisabled, setSelectedPatientChangesDisabled] = useState(true)
  const [tryBookingScreening, setTryBookingScreening] = useState(true)

  const bookingDialogTitle = useMemo(() => {
    if (isNewPatient && !selectedPatient) {
      return patientFormCopy.createPatientTitle
    }
    if (!selectedPatientChangesDisabled) return 'Edit Patient'
    if (selectedPatient) {
      const mentorTitle = (() => {
        if (Array.isArray(mentorProfileData?.categories)) {
          if (
            mentorProfileData.categories.includes('therapist') ||
            mentorProfileData.categories.includes('clinical_psychologist') ||
            mentorProfileData.categories.includes('counselling_psychologist')
          ) {
            return 'Therapist'
          }
          if (mentorProfileData.categories.includes('doctor')) {
            return 'Doctor'
          }
        }
        return 'Mentor'
      })()
      return `Booking Appointment with ${mentorProfileData?.name || mentorTitle}`
    }
    return 'Search or Create Patient'
  }, [
    isNewPatient,
    mentorProfileData?.categories,
    mentorProfileData?.name,
    patientFormCopy.createPatientTitle,
    selectedPatient,
    selectedPatientChangesDisabled,
  ])

  const [newPatientSubmissionLoading, setNewPatientSubmissionLoading] = useState(false)
  const [bookingLoading, setBookingLoading] = useState(false)
  const [patientUpdateLoading, setPatientUpdateLoading] = useState(false)

  const [selectedRelatedConsultationForTherapy, setSelectedRelatedConsultationForTherapy] =
    useState('')
  const [pastConsultationsOfPatient, setPastConsultationsOfPatient] = useState([])

  const [newPatientInputData, setNewPatientInputData] = useState({
    ...BLANK_PATIENT_DATA,
    defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
  })
  const [newPatientInputDataValidations, setNewPatientInputDataValidations] =
    useState(PATIENT_VALIDATION_FIELDS)


  const [paymentDetail, setPaymentDetail] = useState({
    feePaid: '',
    feePaymentMode: 'cash',
    feePaidTxnId: '',
    currency: planDataForSelectedEventId?.currency || 'inr',
    feeCollectionNote: '',
  })

  // Sync currency when appointment type is selected (handles case when appointment type loads after component mount)
  useEffect(() => {
    if (planDataForSelectedEventId?.currency && planDataForSelectedEventId.currency.toLowerCase() !== paymentDetail.currency) {
      setPaymentDetail((prev) => ({
        ...prev,
        currency: planDataForSelectedEventId.currency.toLowerCase(),
      }))
    }
  }, [planDataForSelectedEventId?.currency, paymentDetail.currency])

  let paymentDetailToSubmit = {
    ...paymentDetail,
  }
  if (clinicBrandData?.bookingFormSettings?.hidePricingDetailsInBookingForm) {
    paymentDetailToSubmit.feePaid = planDataForSelectedEventId?.finalPrice
    paymentDetailToSubmit.feePaymentMode = 'cash'
    paymentDetailToSubmit.currency = planDataForSelectedEventId?.currency || 'inr'
  }

  console.log({ clinicBrandData })

  const [autoFillStuffForNewPatientString, setAutoFillStuffForNewPatientString] = useState('')

  const [chosenChamberRoomId, setChosenChamberRoomId] = useState('')

  const [showPastBookings, setShowPastBookings] = useState(false)
  const [showVitals, setShowVitals] = useState(false)

  const [makeAnonymousPatientField, setMakeAnonymousPatient] = useState(false)

  const makeAnonymousPatient = forceAnonymousPatient || makeAnonymousPatientField

  const [
    chamberRoomsForMentorAssistantClinicLocation,
    setChamberRoomsForMentorAssistantClinicLocation,
  ] = useState([])

  const [reasonForNotDoingScreening, setReasonForNotDoingScreening] = useState('')
  const [reasonIdForNotDoingScreening, setReasonIdForNotDoingScreening] = useState('')
  const [screenerAvailable, setScreenerAvailable] = useState(false)

  const [relatedConsultationsLoading, setRelatedConsultationsLoading] = useState(false)

  const [printReceipt, setPrintReceipt] = useState(false)

  const [patientSaveMessage, setPatientSaveMessage] = useState({
    type: '',
    message: '',
  })

  const [importedFromIdItems, setImportedFromIdItems] = useState([])

  const [requestConfigurationLoading, setRequestConfigurationLoading] = useState(false)

  const patientInfoSelectRef = useRef()

  // Edit Session Start Dialog State
  const [editSessionStartDialogOpen, setEditSessionStartDialogOpen] = useState(false)
  const [selectedCustomTime, setSelectedCustomTime] = useState('')
  const [selectedSlotTime, setSelectedSlotTime] = useState(null)

  // Detect current route
  const getCurrentRoute = () => {
    if (typeof window === 'undefined') return 'unknown'
    const pathname = window.location?.pathname || ''
    if (pathname.includes('/mentor-home')) return 'mentor-home'
    if (pathname.includes('/ca-home') || pathname.includes('/kiosk')) return 'ca-home'
    if (pathname.includes('/support-home')) return 'support-home'
    return 'unknown'
  }

  const currentRoute = getCurrentRoute()

  // Function to request event configuration
  async function requestEventConfiguration(notifyMentor = false) {
    setRequestConfigurationLoading(true)
    try {
      const requestBody = {
        clinicId,
        clinicBrandId,
        mentorId: mentorProfileData?.id,
        mentorName: mentorProfileData?.name,
        notifyAumhumTeam: true,
        notifyMentor: notifyMentor,
        requestingPortal: currentRoute,
        message: `No events (Consultations/Therapy/Assessment) are configured for ${mentorProfileData?.name || 'this practitioner'}`,
      }

      const response = await postCPDataWithoutParams(
        '/api/secure/clinic/request-event-configuration',
        requestBody,
      )

      if (response?.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
        toast.success('Request sent successfully. The AUMHUM team will be notified.')
      } else {
        toast.error('Failed to send request. Please try again or contact support.')
      }
    } catch (error) {
      console.error('Error requesting event configuration:', error)
      toast.error('Failed to send request. Please try again or contact support.')
    } finally {
      setRequestConfigurationLoading(false)
    }
  }

  useEffect(() => {
    let timeoutId
    if (patientSaveMessage) {
      timeoutId = setTimeout(() => {
        setPatientSaveMessage({
          type: '',
          message: '',
        })
      }, 5000)
    }
    return () => clearTimeout(timeoutId)
  }, [patientSaveMessage])

  // is time enough to book?
  let isEnoughTimeToBook

  // Check if there are valid available events (consultation, therapy, oneOnOne, or assessment)
  // Note: Screening events are NOT considered valid for this check
  const hasValidAvailableEvents = Array.isArray(eventTypesVisible) && eventTypesVisible.length > 0

  let thereIsOverlap = [] // with booked slots
  bookedSlotsInDay?.forEach((nonAvailableSlot) => {
    const UnavailableTimeSlotStart = dayjs(nonAvailableSlot?.startTimeForDayCal)
    const UnavailableTimeSlotEnd = dayjs(nonAvailableSlot?.endTimeForDayCal)

    if (
      dayjs(clickedWantedSlotData?.startTime)?.isSameOrAfter(UnavailableTimeSlotEnd) ||
      dayjs(clickedWantedSlotData?.endTime)?.isSameOrBefore(UnavailableTimeSlotStart)
    ) {
      thereIsOverlap?.push(false)
    } else {
      thereIsOverlap?.push(true)
    }
  })
  isEnoughTimeToBook = !thereIsOverlap?.some((element) => Boolean(element))
  ////////////

  // ask payment?
  const askPayment = true

  const canSubmitNewPatient =
    isValidPatientInfoForCreation(newPatientInputData, makeAnonymousPatient) &&
    !newPatientSubmissionLoading

  async function handleCreatePatientSubmission() {
    const createdPatient = await createNewPatient(
      clinicId,
      clinicBrandId,
      mentorProfileData,
      newPatientInputData,
      setNewPatientSubmissionLoading,
      forMentor,
      setSelectedPatient,
      setNewPatientInputData,
      setIsNewPatient,
      setRelatedToPatient,
      setRelatedPatientLoading,
      makeAnonymousPatient,
      createPatientApiSignature,
      patientSearchApiSignature,
      setPatientSaveMessage,
      clinicBrandData?.patientFormSettings?.exposePatientImportIdForm,
      clinicBrandData?.patientFormSettings?.currentlySetPatientImportIdType,
    )

    if (paymentLinkToSendViaQRCode) {
      paymentLinkToSendViaQRCode({
        currentPatient: createdPatient,
      })
    }
  }

  async function selectPatientFunction(patientDataFromSelect) {
    const selectedPatientData = { ...patientDataFromSelect }
    setIsNewPatient(false)

    const toSetSelectedPatientData =
      buildPatientDataForSelectingForNewBookingCalendar(selectedPatientData)

    setSelectedPatient(toSetSelectedPatientData)
    setRelatedToPatient(null)
    setSelectedPatientChangesDisabled(true)
    if (selectedPatientData?.primaryPatientProfile?.patientId) {
      setRelatedPatientLoading(true)
      let finalSignatureToUse = patientSearchApiSignature
      if (!finalSignatureToUse?.includes('?')) {
        finalSignatureToUse = finalSignatureToUse + '?'
      }
      if (finalSignatureToUse.endsWith('?')) {
        finalSignatureToUse += `patientId=${selectedPatientData?.primaryPatientProfile?.patientId}&clinicId=${clinicId}`
      } else {
        finalSignatureToUse += `&patientId=${selectedPatientData?.primaryPatientProfile?.patientId}&clinicId=${clinicId}`
      }

      // patientSearchApiSignature

      const response = await getCPDataWithoutParams(finalSignatureToUse)
      setRelatedPatientLoading(false)
      if (response.status === 200 && response?.data && response?.data?.[0]) {
        setRelatedToPatient(response?.data?.[0])
      } else {
        toast.error('Error finding related patient data. Contact support.')
      }
    }
    if (selectedPatientData?.id) {
      setSelectedRelatedConsultationForTherapy('')
      setPastConsultationsOfPatient([])
      if (!forMentor) {
        if (eventDataForSelectedEventType?.type === allAvailableEventTypes.therapySession) {
          setRelatedConsultationsLoading(true)
          const response = await getCPDataWithoutParams(
            `/api/secure/clinic-assistant/booked-slots/patient?patientId=${selectedPatientData?.id}&clinicId=${clinicId}&sortDescendingOnStartTime=true`,
          )
          setRelatedConsultationsLoading(false)
          if (response.status === 200 && isCommonResponseSuccessful(response?.data?.code)) {
            let arrayOfPastBookings = response?.data?.data?.['booked-event-info']
            arrayOfPastBookings = arrayOfPastBookings?.filter(
              (item) => item?.eventType === allAvailableEventTypes.consultation,
            )
            setPastConsultationsOfPatient(arrayOfPastBookings)
            if (arrayOfPastBookings?.length > 0) {
              if (isWithinLast24Hours(arrayOfPastBookings?.[0]?.startTime)) {
                setSelectedRelatedConsultationForTherapy(arrayOfPastBookings?.[0]?.id)
              }
            }
          } else {
            toast.error('Error finding related patient data. Contact support.')
          }
        }
      }
    }
  }

  function selectRelatedPatient(switchToPatientData) {
    const switchToPatientDataHere = { ...switchToPatientData }
    setSelectedPatient(switchToPatientDataHere)
    setRelatedToPatient(false)
  }

  function handleAddNewButtonClick(autoFillStuffForNewPatientString) {
    setSelectedPatient(null)
    setIsNewPatient(true)
    if (identifyStringUseForAutoFill(autoFillStuffForNewPatientString).isValid) {
      if (identifyStringUseForAutoFill(autoFillStuffForNewPatientString).type === 'email') {
        setNewPatientInputData({
          ...BLANK_PATIENT_DATA,
          email: autoFillStuffForNewPatientString,
          defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
        })
      } else if (identifyStringUseForAutoFill(autoFillStuffForNewPatientString).type === 'mobile') {
        if (isValidPhoneNumber(`+${autoFillStuffForNewPatientString}`)) {
          setNewPatientInputData({
            ...BLANK_PATIENT_DATA,
            mobile: autoFillStuffForNewPatientString,
            defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
          })
        } else {
          setNewPatientInputData({
            ...BLANK_PATIENT_DATA,
            mobile: `91${autoFillStuffForNewPatientString}`,
            defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
          })
        }
      } else if (identifyStringUseForAutoFill(autoFillStuffForNewPatientString).type === 'name') {
        setNewPatientInputData({
          ...BLANK_PATIENT_DATA,
          name: autoFillStuffForNewPatientString,
          defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
        })
      } else {
        setNewPatientInputData({
          ...BLANK_PATIENT_DATA,
          defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
        })
      }
    } else {
      setNewPatientInputData({
        ...BLANK_PATIENT_DATA,
        defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
      })
    }
  }

  function handleAddNewRelatedPatient(existingPatient, blankPatientData = BLANK_PATIENT_DATA) {
    setIsNewPatient(true)
    setRelatedToPatient(null)
    setSelectedPatient(null)

    const relatedPatientData = buildRelatedPatientData(
      existingPatient,
      blankPatientData,
      clinicBrandData,
    )
    setNewPatientInputData(relatedPatientData)
  }

  async function bookNowHandler() {
    const createdEvent = await makeBookingInDayViewCalendar(
      forMentor,
      clinicId,
      clinicBrandId,
      mentorProfileData?.id,
      askPayment,
      clickedWantedSlotData,
      eventDataForSelectedEventType,
      selectedPatient,
      updateBookingsInCalendar,
      setNewBookingDialogOpen,
      {
        feePaid: paymentDetailToSubmit?.feePaid,
        feePaymentMode: paymentDetailToSubmit?.feePaymentMode,
        feePaidTxnId: paymentDetailToSubmit?.feePaidTxnId,
        currency: paymentDetailToSubmit?.currency,
        feeCollectionNote: paymentDetailToSubmit?.feeCollectionNote || '',
      },
      setBookingLoading,
      setSelectedPatient,
      setIsNewPatient,
      setNewPatientInputData,
      chosenChamberRoomId,
      tryBookingScreening,
      false,
      getCorrectScreeningReasonMessageObject({
        eventType: eventDataForSelectedEventType?.type,
        isScreenerAvailable: screenerAvailable,
        isTryingToBookScreening: tryBookingScreening,
        reasonIdForNotDoingScreening: reasonIdForNotDoingScreening,
        reasonForNotDoingScreening: reasonForNotDoingScreening,
      }),
      null,
      selectedRelatedConsultationForTherapy,
      meetingBookApiSignature,
    )
    await attemptToBindLatestTodayVitalToThisBooking(
      createdEvent?.id,
      createdEvent?.startTime,
      selectedPatient?.id,
      selectedPatient?.clinicId,
    )
    if (printReceipt && !!createdEvent) {
      setEventDataForReceiptPrinting(createdEvent)
      openReceiptDialog(true)
    }
  }

  function resetSelectedPatientFunction() {
    setSelectedPatient(null)
    setRelatedToPatient(null)
    setIsNewPatient(false)
    setSelectedPatientChangesDisabled(true)
    setNewPatientInputData({
      ...BLANK_PATIENT_DATA,
      defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
    })
  }

  useEffect(() => {
    setNewPatientInputDataValidations({
      ...PATIENT_VALIDATION_FIELDS,
      name: Boolean(newPatientInputData?.name),
      email: isPatientEmailValid(newPatientInputData, makeAnonymousPatient),
      mobile: isPatientMobileValid(newPatientInputData, makeAnonymousPatient),
      gender: Boolean(newPatientInputData?.gender),
      age: hasValidPatientAgeInput(newPatientInputData),
    })
  }, [makeAnonymousPatient, newPatientInputData])

  useEffect(() => {
    setSelectedPatientInputDataValidations({
      ...PATIENT_VALIDATION_FIELDS,
      name: Boolean(selectedPatient?.name),
      email: validateEmail(selectedPatient?.email),
      mobile: isValidPhoneNumber(`+${removePlusFromBeginning(selectedPatient?.mobile)}`),
      gender: Boolean(selectedPatient?.gender),
      age: hasValidPatientAgeInput(selectedPatient),
    })
  }, [selectedPatient])

  useEffect(() => {
    if (
      Array.isArray(chambersData) &&
      eventDataForSelectedEventType?.meetingSrc === 'offline' &&
      !forMentor
    ) {
      const items = chambersData?.filter(
        (item) =>
          item?.clinicLocationId === clinicId &&
          item?.inPersonMentorsIds?.includes(mentorProfileData?.id) &&
          item?.clinicAssistantsIds?.includes(assistantProfileInThisClinic?.id),
      )
      if (items?.length > 0) {
        setChosenChamberRoomId(items?.[0]?.id)
        setChamberRoomsForMentorAssistantClinicLocation(items)
      } else {
        setChosenChamberRoomId('')
        setChamberRoomsForMentorAssistantClinicLocation([])
      }
    } else {
      setChosenChamberRoomId('')
      setChamberRoomsForMentorAssistantClinicLocation([])
    }
  }, [chambersData, mentorProfileData, eventDataForSelectedEventType, clinicId])

  useEffect(() => {
    setClickedWantedSlotData((prev) => {
      return {
        ...prev,
        endTime: dayjs(prev?.startTime)
          .add(eventDataForSelectedEventType?.durationInMins, 'minute')
          .format('YYYY-MM-DDTHH:mm:ssZ'),
      }
    })
  }, [selectedEventTypeId, eventDataForSelectedEventType])

  useEffect(() => {
    const sessionPrintReceipt = sessionStorage.getItem('printReceipt')
    if (patientInfoSelectRef.current) {
      patientInfoSelectRef.current.focus()
    }
    setPrintReceipt(sessionPrintReceipt === 'true' ? true : false)
  }, [])

  console.log({ selectedPatient, relatedToPatient })

  console.log({
    importedFromItem: importedFromItems?.find(
      (item) => item?.id === clinicBrandData?.patientFormSettings?.currentlySetPatientImportIdType,
    ),
  })

  function render() {
    return (
      <EDVCNewBookingContainer>
        {clinicId ? (
          <>
            {/* section one */}
            {!(isNewPatient && !selectedPatient) &&
              selectedPatientChangesDisabled &&
              !selectedPatientOverride && (
                <>
                  {!hasValidAvailableEvents ? (
                    <SearchSectionParent>
                      <div
                        style={{
                          padding: '24px',
                          textAlign: 'center',
                          backgroundColor: '#fff3cd',
                          borderRadius: '8px',
                          border: '1px solid #ffc107',
                        }}
                      >
                        <div
                          style={{
                            fontSize: '16px',
                            fontWeight: '500',
                            marginBottom: '12px',
                            color: '#856404',
                          }}
                        >
                          No events (Consultations/Therapy/Assessment) are configured for{' '}
                          {mentorProfileData?.name || 'this practitioner'}
                        </div>
                        <div
                          style={{
                            fontSize: '14px',
                            marginBottom: '16px',
                            color: '#856404',
                          }}
                        >
                          {currentRoute === 'mentor-home' && (
                            <>Please contact the AUMHUM team to configure events for this clinic location.</>
                          )}
                          {currentRoute === 'ca-home' && (
                            <>
                              Please contact the AUMHUM team and {mentorProfileData?.name || 'the practitioner'} to configure events for this clinic location.
                            </>
                          )}
                          {currentRoute === 'support-home' && (
                            <>
                              Please contact the AUMHUM team and {mentorProfileData?.name || 'the practitioner'} to configure events for this clinic location.
                            </>
                          )}
                          {currentRoute === 'unknown' && (
                            <>Please contact the AUMHUM team to configure events for this clinic location.</>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <MuiButton
                            variant="contained"
                            color="primary"
                            disabled={requestConfigurationLoading}
                            onClick={() => {
                              if (currentRoute === 'mentor-home') {
                                requestEventConfiguration(false)
                              } else if (currentRoute === 'ca-home') {
                                requestEventConfiguration(true)
                              } else if (currentRoute === 'support-home') {
                                requestEventConfiguration(true)
                              } else {
                                requestEventConfiguration(false)
                              }
                            }}
                            sx={{ minWidth: '200px' }}
                          >
                            {requestConfigurationLoading ? (
                              <>
                                <Spinner animation="border" variant="dark" size="sm" style={{ marginRight: '8px' }} />
                                Loading...
                              </>
                            ) : currentRoute === 'mentor-home' ? (
                              'Ask AUMHUM Team to Configure'
                            ) : currentRoute === 'ca-home' || currentRoute === 'support-home' ? (
                              'Ask AUMHUM Team & Practitioner to Configure'
                            ) : (
                              'Ask AUMHUM Team to Configure'
                            )}
                          </MuiButton>
                          {currentRoute === 'support-home' && (
                            <MuiButton
                              variant="outlined"
                              color="primary"
                              disabled={requestConfigurationLoading}
                              onClick={() => requestEventConfiguration(false)}
                              sx={{ minWidth: '200px' }}
                            >
                              Ask AUMHUM Team Only
                            </MuiButton>
                          )}
                        </div>
                      </div>
                    </SearchSectionParent>
                  ) : !selectedPatient ? (
                    <SearchSectionParent>
                      <GenericDisplayFlex
                        style={{
                          justifyContent: 'space-between',
                          gap: '15px',
                          alignItems: 'flex-start',
                        }}
                      >
                        <div style={{ width: '100%' }}>
                          <AsyncPaginateWithRef
                            ref={patientInfoSelectRef}
                            value={selectedPatient}
                            isClearable={true}
                            placeholder="Search Patient"
                            loadOptions={loadOptionsForExistingPatient}
                            components={{
                              NoOptionsMessage: cmbcCustomNoOptionsMessageComponentFunction(
                                autoFillStuffForNewPatientString,
                                handleAddNewButtonClick,
                              ),
                              Placeholder: PatientSearchPlaceholder,
                              Option: PatientSearchCustomOption,
                              ClearIndicator: cmbcCustomResetClearFuncComponent(
                                resetSelectedPatientFunction,
                              ),
                            }}
                            styles={{
                              menuList: (provided, state) => ({
                                ...provided,
                                maxHeight: '400px', // Increased to show more entries
                                overflowY: 'auto',
                              }),
                              dropdownIndicator: (provided) => ({
                                ...provided,
                                display: 'none', // Hides the dropdown arrow
                              }),
                              indicatorSeparator: (provided) => ({
                                ...provided,
                                display: 'none', // Optionally hides the separator line
                              }),
                            }}
                            getOptionValue={(option) => option.id}
                            getOptionLabel={(option) => getLabelForAlreadyPatientOption(option)}
                            onChange={selectPatientFunction}
                            debounceTimeout={mentorAuthorisedToSaveAndGetPatientsUsingIdb ? 200 : 500}
                            additional={{
                              page: 1,
                              clinicId,
                              clinicBrandId,
                              patientSearchApiSignature,
                              setAutoFillStuffForNewPatientString,
                              mentorAuthorisedToSaveAndGetPatientsUsingIdb,
                              searchOnImportId:
                                clinicBrandData?.patientFormSettings?.exposePatientImportIdForm,
                              searchOnUhId: clinicBrandData?.patientFormSettings?.exposePatientUhIdForm,
                            }}
                          />
                          <div style={{ fontSize: '14px' }}>
                            <i>Use Patient No./Name/Mobile/Email</i>
                          </div>
                        </div>
                        {/* <span style={{ marginTop: '6px' }}>{' or '}</span> */}
                        {!isNewPatient && !selectedPatient && (
                          <MuiButton
                            variant="contained"
                            sx={{ flexShrink: 0 }}
                            onClick={() => {
                              handleAddNewButtonClick(autoFillStuffForNewPatientString)
                            }}
                          >
                            Add New
                          </MuiButton>
                        )}
                        {/* <MuiButton variant="text" onClick={resetSelectedPatientFunction}>
                  Reset
                </MuiButton> */}
                      </GenericDisplayFlex>
                    </SearchSectionParent>
                  ) : null}
                </>
              )}

            {/* isNewPatient is marked true and there is no selected patient, patient creation possible */}
            {isNewPatient && !selectedPatient && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(71, 85, 105, 0.92)',
                      fontSize: '13px',
                    }}
                  >
                    {patientFormCopy.createPatientSubtitle}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    pb: {
                      xs: 'calc(112px + env(safe-area-inset-bottom, 0px))',
                      sm: '104px',
                    },
                  }}
                >
                  <PatientFormComponent
                    editMode
                    patientDetails={newPatientInputData}
                    setPatientDetails={setNewPatientInputData}
                    patientInputDataValidations={newPatientInputDataValidations}
                    hidePatientNumber
                    makeAnonymousPatient={makeAnonymousPatient}
                    clinicId={clinicId}
                    clinicBrandId={clinicBrandId}
                    addRelatedPatient={handleAddNewRelatedPatient}
                    onFindingExistingPatient={(pat) => selectPatientFunction(pat)}
                    setMakeAnonymousPatient={setMakeAnonymousPatient}
                    isForcingAnonymousPatient={forceAnonymousPatient}
                    isAllowedToAddImportId={
                      clinicBrandData?.patientFormSettings?.exposePatientImportIdForm
                    }
                    moveEmailToMoreSettings={
                      clinicBrandData?.patientFormSettings?.hideEmailInTheForm
                    }
                    isAllowedToAddUHID={clinicBrandData?.patientFormSettings?.exposePatientUhIdForm}
                    importIdFieldLabel={
                      importedFromItems?.find(
                        (item) =>
                          item?.id ===
                          clinicBrandData?.patientFormSettings?.currentlySetPatientImportIdType,
                      )?.title
                    }
                    autoFocusMobileField={open && isNewPatient}
                    patientSearchApiSignature={patientSearchApiSignature}
                    formUiLanguageCode={patientFormUiLanguage}
                  />
                </Box>
                <Box
                  sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 12,
                    mt: 1,
                    pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
                    pt: 1.25,
                    px: 1.25,
                    borderTop: '1px solid rgba(67, 56, 202, 0.12)',
                    borderRadius: '16px 16px 0 0',
                    background:
                      'linear-gradient(180deg, rgba(255, 255, 255, 0.86) 0%, rgba(255, 255, 255, 0.97) 22%, #ffffff 100%)',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.08)',
                  }}
                >
                  <Box
                    sx={{
                      minHeight: patientSaveMessage?.message ? '20px' : '18px',
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      color:
                        patientSaveMessage?.type === 'error'
                          ? '#b73c30'
                          : 'rgba(71, 85, 105, 0.9)',
                      fontSize: '12px',
                      lineHeight: 1.45,
                    }}
                  >
                    {patientSaveMessage?.message ? (
                      <>
                        {patientSaveMessage.type === 'error' && (
                          <InfoIcon sx={{ fontSize: '16px', color: '#b73c30' }} />
                        )}
                        <span>{patientSaveMessage.message}</span>
                      </>
                    ) : (
                      <span>{patientFormCopy.createPatientStickyHint}</span>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 1,
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    }}
                  >
                    <MuiButton
                      variant="outlined"
                      disabled={newPatientSubmissionLoading}
                      onClick={resetSelectedPatientFunction}
                      sx={{
                        minWidth: { xs: '100%', sm: '120px' },
                        order: { xs: 2, sm: 1 },
                      }}
                    >
                      {patientFormCopy.cancelButton}
                    </MuiButton>
                    <MuiButton
                      variant="contained"
                      disabled={!canSubmitNewPatient}
                      onClick={handleCreatePatientSubmission}
                      sx={{
                        minWidth: { xs: '100%', sm: '150px' },
                        order: { xs: 1, sm: 2 },
                      }}
                    >
                      {newPatientSubmissionLoading
                        ? patientFormCopy.addingPatientButton
                        : patientFormCopy.addPatientButton}
                    </MuiButton>
                  </Box>
                </Box>
              </>
            )}

            {/* patient is selected and either below code is used to either update the details of the patient or create a booking for him */}
            {selectedPatient && (
              <>
                {!selectedPatientChangesDisabled && (
                  <div
                    style={{
                      marginBottom: '16px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                    }}
                  >
                    <MuiButton
                      variant="outlined"
                      disabled={patientUpdateLoading}
                      onClick={() => {
                        setSelectedPatientChangesDisabled(true)
                      }}
                    >
                      Cancel
                    </MuiButton>
                    {patientUpdateLoading ? (
                      <Spinner animation="border" variant="dark" size="sm" />
                    ) : (
                      <MuiButton
                        variant="contained"
                        disabled={(() => {
                          if (!selectedPatientChangesDisabled) {
                            return (
                              !(
                                selectedPatientInputDataValidations?.name &&
                                (selectedPatientInputDataValidations?.email ||
                                  selectedPatientInputDataValidations?.mobile) &&
                                selectedPatientInputDataValidations?.gender &&
                                selectedPatientInputDataValidations?.age
                              ) || patientUpdateLoading
                            )
                          }
                          return false
                        })()}
                        onClick={() => {
                          handleUpdatePatientDetails(
                            selectedPatient?.id,
                            clinicId,
                            clinicBrandId,
                            mentorProfileData,
                            selectedPatient,
                            setPatientUpdateLoading,
                            setSelectedPatientChangesDisabled,
                            patientUpdateApiSignature,
                          )
                        }}
                      >
                        {!selectedPatientChangesDisabled && 'Update Patient'}
                      </MuiButton>
                    )}
                  </div>
                )}

                {/* Compact Patient Details Section */}
                <Box
                  sx={{
                    border: '1.5px solid rgba(67, 56, 202, 0.15)',
                    borderRadius: '10px',
                    padding: '14px 16px',
                    marginBottom: '12px',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 6px rgba(67, 56, 202, 0.08)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '8px',
                      flexWrap: 'wrap',
                      gap: '6px',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontSize: '13px',
                        fontWeight: 700,
                        color: 'rgba(67, 56, 202, 0.9)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.8px',
                      }}
                    >
                      Patient Details
                    </Typography>
                    {selectedPatientChangesDisabled && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <MuiButton
                          size="small"
                          startIcon={<EditIcon sx={{ fontSize: '16px' }} />}
                          variant="outlined"
                          onClick={() => {
                            setSelectedPatientChangesDisabled(false)
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault()
                            setSelectedPatientChangesDisabled(false)
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            padding: '6px 14px',
                            minWidth: 'auto',
                            height: '36px',
                            borderWidth: '1.5px',
                            borderColor: 'rgba(67, 56, 202, 0.4)',
                            color: 'rgba(67, 56, 202, 0.9)',
                            fontWeight: 600,
                            touchAction: 'manipulation',
                            borderRadius: '8px',
                            '&:hover': {
                              borderColor: 'rgba(67, 56, 202, 0.6)',
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              color: 'rgba(67, 56, 202, 1)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
                            },
                            '&:active': {
                              backgroundColor: 'rgba(67, 56, 202, 0.12)',
                              transform: 'translateY(0)',
                            },
                          }}
                        >
                          Edit
                        </MuiButton>
                        {selectedPatient &&
                          selectedPatientChangesDisabled &&
                          !selectedPatient?.primaryPatientProfile?.patientId && (
                            <MuiButton
                              size="small"
                              endIcon={<AddIcon sx={{ fontSize: '16px' }} />}
                              variant="outlined"
                              onClick={() => handleAddNewRelatedPatient(selectedPatient)}
                              onTouchStart={(e) => {
                                e.preventDefault()
                                handleAddNewRelatedPatient(selectedPatient)
                              }}
                              sx={{
                                textTransform: 'none',
                                fontSize: '13px',
                                padding: '6px 14px',
                                minWidth: 'auto',
                                height: '36px',
                                borderWidth: '1.5px',
                                borderColor: 'rgba(67, 56, 202, 0.4)',
                                color: 'rgba(67, 56, 202, 0.9)',
                                fontWeight: 600,
                                touchAction: 'manipulation',
                                borderRadius: '8px',
                                '&:hover': {
                                  borderColor: 'rgba(67, 56, 202, 0.6)',
                                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                  color: 'rgba(67, 56, 202, 1)',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
                                },
                                '&:active': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.12)',
                                  transform: 'translateY(0)',
                                },
                              }}
                            >
                              Add Relative
                            </MuiButton>
                          )}
                      </div>
                    )}
                  </div>

                  {selectedPatientChangesDisabled ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {/* Line 1: Name (Age/Gender) | Patient No */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '1 1 auto', minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: 'rgba(67, 56, 202, 1)',
                              fontSize: '15px',
                              lineHeight: 1.4,
                            }}
                          >
                            {selectedPatient?.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'rgba(67, 56, 202, 0.7)',
                              fontSize: '14px',
                              fontWeight: 500,
                            }}
                          >
                            ({formatPatientAgeSummary(selectedPatient) || '?'}/
                            {GENDER_LIST_OFFLINE.find(
                              (item) => item.value === selectedPatient?.gender,
                            )?.label || '?'}
                            )
                          </Typography>
                        </div>
                        {selectedPatient?.patientNumber && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', backgroundColor: 'rgba(67, 56, 202, 0.05)', borderRadius: '6px' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.6)',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}
                            >
                              No:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.9)',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              {selectedPatient.patientNumber}
                            </Typography>
                          </div>
                        )}
                      </div>

                      {/* Line 2: Contact | City */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '4px' }}>
                        {(selectedPatient?.mobile || selectedPatient?.email) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto', minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.6)',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              Contact:
                            </Typography>
                            {selectedPatient?.mobile && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'rgba(67, 56, 202, 0.9)',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                }}
                              >
                                {selectedPatient.mobile}
                              </Typography>
                            )}
                            {selectedPatient?.mobile && selectedPatient?.email && (
                              <span style={{ color: 'rgba(67, 56, 202, 0.4)', fontSize: '12px', fontWeight: 600 }}>•</span>
                            )}
                            {selectedPatient?.email && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'rgba(67, 56, 202, 0.9)',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: '200px',
                                }}
                              >
                                {selectedPatient.email}
                              </Typography>
                            )}
                          </div>
                        )}
                        {selectedPatient?.city && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', backgroundColor: 'rgba(67, 56, 202, 0.05)', borderRadius: '6px' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.6)',
                                fontSize: '11px',
                                fontWeight: 600,
                              }}
                            >
                              City:
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.9)',
                                fontSize: '12px',
                                fontWeight: 600,
                              }}
                            >
                              {selectedPatient.city}
                            </Typography>
                          </div>
                        )}
                      </div>

                      {/* Related Patient Section */}
                      {selectedPatient?.primaryPatientProfile?.patientId &&
                        (relatedPatientLoading ? (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 10px',
                              backgroundColor: '#fff7ed',
                              borderRadius: '6px',
                              border: '1px solid #fed7aa',
                              marginTop: '2px',
                            }}
                          >
                            <Spinner animation="border" variant="dark" size="sm" />
                            <Typography variant="caption" sx={{ color: '#9a3412', fontSize: '10px' }}>
                              Loading related patient...
                            </Typography>
                          </Box>
                        ) : (
                          relatedToPatient && (
                            <Box
                              sx={{
                                padding: '6px 10px',
                                backgroundColor: '#f0f9ff',
                                borderRadius: '6px',
                                border: '1px solid #bae6fd',
                                marginTop: '2px',
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 500,
                                  color: '#0369a1',
                                  fontSize: '10px',
                                }}
                              >
                                <span style={{ color: 'rgba(67, 56, 202, 0.6)' }}>
                                  ({relatedToPatient?.patientNumber})
                                </span>
                                {` ${relatedToPatient?.name}'s ${selectedPatient?.primaryPatientProfile?.dependentRelation}`}
                              </Typography>
                            </Box>
                          )
                        ))}
                    </div>
                  ) : (
                    <EditPatientForm
                      selectedPatient={selectedPatient}
                      setSelectedPatient={setSelectedPatient}
                      selectedPatientChangesDisabled={selectedPatientChangesDisabled}
                      selectedPatientInputDataValidations={selectedPatientInputDataValidations}
                      clinicBrandData={clinicBrandData}
                      importedFromItems={importedFromItems}
                      clinicId={clinicId}
                      clinicBrandId={clinicBrandId}
                      patientSearchApiSignature={patientSearchApiSignature}
                    />
                  )}
                </Box>

                {/* Vitals and Past Bookings - Side by Side Buttons when Collapsed */}
                {selectedPatientChangesDisabled && !showVitals && !showPastBookings && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '10px',
                      flexWrap: 'wrap',
                    }}
                  >
                    <MuiButton
                      variant="outlined"
                      size="medium"
                      onClick={() => setShowVitals(true)}
                      onTouchStart={(e) => {
                        e.preventDefault()
                        setShowVitals(true)
                      }}
                      sx={{
                        flex: '1 1 auto',
                        minWidth: '140px',
                        textTransform: 'none',
                        fontSize: '14px',
                        padding: '10px 16px',
                        height: '42px',
                        borderWidth: '1.5px',
                        borderColor: 'rgba(67, 56, 202, 0.4)',
                        color: 'rgba(67, 56, 202, 0.9)',
                        fontWeight: 600,
                        touchAction: 'manipulation',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: 'rgba(67, 56, 202, 0.6)',
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
                        },
                        '&:active': {
                          backgroundColor: 'rgba(67, 56, 202, 0.12)',
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Add Vitals
                    </MuiButton>
                    <MuiButton
                      variant="outlined"
                      size="medium"
                      onClick={() => setShowPastBookings(true)}
                      onTouchStart={(e) => {
                        e.preventDefault()
                        setShowPastBookings(true)
                      }}
                      sx={{
                        flex: '1 1 auto',
                        minWidth: '140px',
                        textTransform: 'none',
                        fontSize: '14px',
                        padding: '10px 16px',
                        height: '42px',
                        borderWidth: '1.5px',
                        borderColor: 'rgba(67, 56, 202, 0.4)',
                        color: 'rgba(67, 56, 202, 0.9)',
                        fontWeight: 600,
                        touchAction: 'manipulation',
                        borderRadius: '8px',
                        '&:hover': {
                          borderColor: 'rgba(67, 56, 202, 0.6)',
                          backgroundColor: 'rgba(67, 56, 202, 0.08)',
                          color: 'rgba(67, 56, 202, 1)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(67, 56, 202, 0.15)',
                        },
                        '&:active': {
                          backgroundColor: 'rgba(67, 56, 202, 0.12)',
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      Show Past Bookings
                    </MuiButton>
                  </Box>
                )}

                {/* Expanded Vitals Section */}
                {selectedPatientChangesDisabled && showVitals && (
                  <Box
                    sx={{
                      border: '1.5px solid rgba(67, 56, 202, 0.15)',
                      borderRadius: '10px',
                      padding: '14px 16px',
                      marginBottom: '12px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 2px 6px rgba(67, 56, 202, 0.08)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: 'rgba(67, 56, 202, 0.7)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Vitals
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setShowVitals(false)}
                        onTouchStart={(e) => {
                          e.preventDefault()
                          setShowVitals(false)
                        }}
                        sx={{
                          padding: '4px',
                          color: 'rgba(67, 56, 202, 0.7)',
                          touchAction: 'manipulation',
                          '&:hover': {
                            backgroundColor: 'rgba(67, 56, 202, 0.05)',
                            color: 'rgba(67, 56, 202, 0.9)',
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: '16px' }} />
                      </IconButton>
                    </div>
                    <VitalsComponentInsideNewBooking
                      patientId={selectedPatient?.id}
                      clinicId={selectedPatient?.clinicId}
                      practitionerId={mentorProfileData?.id}
                      withCreateButton={true}
                      vitalsFormGetApiSignature={vitalsFormGetApiSignature}
                    />
                  </Box>
                )}

                {/* Expanded Past Bookings Section */}
                {selectedPatientChangesDisabled && showPastBookings && (
                  <Box
                    sx={{
                      border: '1.5px solid rgba(67, 56, 202, 0.15)',
                      borderRadius: '10px',
                      padding: '14px 16px',
                      marginBottom: '12px',
                      backgroundColor: '#ffffff',
                      boxShadow: '0 2px 6px rgba(67, 56, 202, 0.08)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: 'rgba(67, 56, 202, 0.9)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.8px',
                              }}
                            >
                              Past Bookings
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => setShowPastBookings(false)}
                              onTouchStart={(e) => {
                                e.preventDefault()
                                setShowPastBookings(false)
                              }}
                              sx={{
                                padding: '6px',
                                color: 'rgba(67, 56, 202, 0.7)',
                                touchAction: 'manipulation',
                                border: '1px solid rgba(67, 56, 202, 0.2)',
                                borderRadius: '6px',
                                '&:hover': {
                                  backgroundColor: 'rgba(67, 56, 202, 0.08)',
                                  color: 'rgba(67, 56, 202, 0.9)',
                                  borderColor: 'rgba(67, 56, 202, 0.4)',
                                },
                              }}
                            >
                              <CloseIcon sx={{ fontSize: '18px' }} />
                            </IconButton>
                    </div>
                    <PastBookingsHorizontalComponent
                      patientId={selectedPatient?.id}
                      clinicId={selectedPatient?.clinicId}
                      pastPatientBookingsApiSignature={pastPatientBookingsApiSignature}
                    />
                  </Box>
                )}

                {selectedPatientChangesDisabled && (
                  <>
                    <Divider sx={{ my: 1.5, borderColor: 'rgba(67, 56, 202, 0.12)' }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 700,
                        color: 'rgba(67, 56, 202, 1)',
                        marginBottom: '12px',
                        marginTop: '6px',
                        letterSpacing: '0.3px',
                      }}
                    >
                      {dialogSecondaryTitle}
                    </Typography>
                    
                    {/* Session Starts - Compact Section */}
                    {selectedPatient && clickedWantedSlotData?.startTime && (
                      <Box
                        sx={{
                          border: '1.5px solid rgba(67, 56, 202, 0.15)',
                          borderRadius: '10px',
                          padding: '14px 16px',
                          marginBottom: '12px',
                          backgroundColor: '#ffffff',
                          boxShadow: '0 2px 6px rgba(67, 56, 202, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'rgba(67, 56, 202, 0.8)',
                                fontSize: '12px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.8px',
                              }}
                            >
                              Session Starts:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'rgba(67, 56, 202, 1)',
                                fontSize: '14px',
                                fontWeight: 600,
                              }}
                            >
                              {(() => {
                                const sessionTime = dayjs(clickedWantedSlotData?.startTime).local()
                                const timeStr = sessionTime.format('DD/MM/YYYY, h:mma')
                                // Get timezone abbreviation
                                let tzAbbr = 'IST'
                                try {
                                  const timezone = clinicData?.schedules?.[0]?.timezone || clinicData?.timeZone?.offset || '+05:30'
                                  const decodedTz = typeof timezone === 'string' && timezone.includes('%') ? decodeURIComponent(timezone) : timezone
                                  if (decodedTz === '+05:30' || decodedTz === 'Asia/Kolkata' || decodedTz === 'Asia/Calcutta') {
                                    tzAbbr = 'IST'
                                  } else if (decodedTz === '+05:00') {
                                    tzAbbr = 'PKT'
                                  } else if (decodedTz === '+08:00') {
                                    tzAbbr = 'CST'
                                  } else if (decodedTz === '+00:00' || decodedTz === 'UTC') {
                                    tzAbbr = 'UTC'
                                  } else {
                                    const offsetMatch = decodedTz.match(/^([+-])(\d{2}):(\d{2})$/)
                                    if (offsetMatch) {
                                      const sign = offsetMatch[1]
                                      const hours = parseInt(offsetMatch[2], 10)
                                      const minutes = parseInt(offsetMatch[3], 10)
                                      tzAbbr = minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${minutes}`
                                    } else {
                                      tzAbbr = decodedTz
                                    }
                                  }
                                } catch {
                                  tzAbbr = 'IST'
                                }
                                
                                // Calculate relative time
                                let relativeTimeStr = ''
                                try {
                                  const now = dayjs()
                                  const diffMinutes = sessionTime.diff(now, 'minute')
                                  const absDiffMinutes = Math.abs(diffMinutes)
                                  
                                  if (absDiffMinutes < 60) {
                                    relativeTimeStr = diffMinutes >= 0 
                                      ? `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''} from now`
                                      : `${absDiffMinutes} min${absDiffMinutes !== 1 ? 's' : ''} ago`
                                  } else if (absDiffMinutes < 1440) {
                                    const hours = Math.floor(absDiffMinutes / 60)
                                    const mins = absDiffMinutes % 60
                                    if (mins === 0) {
                                      relativeTimeStr = diffMinutes >= 0
                                        ? `${hours} hour${hours !== 1 ? 's' : ''} from now`
                                        : `${hours} hour${hours !== 1 ? 's' : ''} ago`
                                    } else {
                                      relativeTimeStr = diffMinutes >= 0
                                        ? `${hours}h ${mins}m from now`
                                        : `${hours}h ${mins}m ago`
                                    }
                                  } else {
                                    const days = Math.floor(absDiffMinutes / 1440)
                                    relativeTimeStr = diffMinutes >= 0
                                      ? `${days} day${days !== 1 ? 's' : ''} from now`
                                      : `${days} day${days !== 1 ? 's' : ''} ago`
                                  }
                                } catch {
                                  relativeTimeStr = ''
                                }
                                
                                return (
                                  <>
                                    {timeStr} ({tzAbbr})
                                    {relativeTimeStr && (
                                      <Typography
                                        component="span"
                                        sx={{
                                          color: 'rgba(67, 56, 202, 0.65)',
                                          fontSize: '11px',
                                          fontWeight: 500,
                                          marginLeft: '8px',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        {relativeTimeStr}
                                      </Typography>
                                    )}
                                  </>
                                )
                              })()}
                            </Typography>
                          </div>
                        </div>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditSessionStartDialogOpen(true)
                            setSelectedCustomTime('')
                            setSelectedSlotTime(null)
                            if (clickedWantedSlotData?.startTime) {
                              const currentTime = dayjs(clickedWantedSlotData.startTime).local()
                              setSelectedCustomTime(currentTime.format('HH:mm'))
                            }
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault()
                            setEditSessionStartDialogOpen(true)
                            setSelectedCustomTime('')
                            setSelectedSlotTime(null)
                            if (clickedWantedSlotData?.startTime) {
                              const currentTime = dayjs(clickedWantedSlotData.startTime).local()
                              setSelectedCustomTime(currentTime.format('HH:mm'))
                            }
                          }}
                          sx={{
                            padding: '8px',
                            color: 'rgba(67, 56, 202, 0.8)',
                            backgroundColor: 'transparent',
                            border: '1.5px solid rgba(67, 56, 202, 0.3)',
                            minWidth: '36px',
                            minHeight: '36px',
                            borderRadius: '8px',
                            '&:hover': {
                              backgroundColor: 'rgba(67, 56, 202, 0.08)',
                              borderColor: 'rgba(67, 56, 202, 0.5)',
                              color: 'rgba(67, 56, 202, 1)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 2px 6px rgba(67, 56, 202, 0.2)',
                            },
                            '&:active': {
                              backgroundColor: 'rgba(67, 56, 202, 0.12)',
                              transform: 'translateY(0)',
                            },
                            transition: 'all 0.2s ease',
                            touchAction: 'manipulation',
                          }}
                        >
                          <EditIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </Box>
                    )}
                    
                    {selectedPatient && (
                      <div style={{ paddingBottom: '10px' }}>
                        <EventSelectorWithVisitEventTypesSelection
                          eventTypesVisible={eventTypesVisible}
                          selectedEventTypeId={selectedEventTypeId}
                          setSelectedEventTypeId={setSelectedEventTypeId}
                          clinicLocationId={clinicId}
                          mentorProfileData={mentorProfileData}
                          selectedVisitType={selectedVisitEventType}
                          setSelectedVisitType={setSelectedVisitEventType}
                        />
                      </div>
                    )}

                    {selectedPatient && !!selectedEventTypeId && (
                      <Box
                        sx={{
                          border: '2px solid rgba(67, 56, 202, 0.35)',
                          borderRadius: '10px',
                          padding: '14px 16px',
                          marginTop: '12px',
                          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.06) 0%, rgba(99, 102, 241, 0.06) 100%)',
                          boxShadow: '0 3px 10px rgba(67, 56, 202, 0.12)',
                          width: '100%',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>
                          <div style={{ marginBottom: '6px' }}>
                            <strong style={{ color: 'rgba(67, 56, 202, 0.9)' }}>Selected Session:</strong>{' '}
                            <span style={{ color: 'rgba(67, 56, 202, 0.8)' }}>{eventDataForSelectedEventType?.title} ({displayNamesOfEventTypes?.[eventDataForSelectedEventType?.type] || ''})</span>
                          </div>
                          {mentorProfileData?.name && (
                            <div style={{ marginTop: '6px' }}>
                              <strong style={{ color: 'rgba(67, 56, 202, 0.9)' }}>
                                {(() => {
                                  if (Array.isArray(mentorProfileData?.categories)) {
                                    if (mentorProfileData.categories.includes('therapist') || mentorProfileData.categories.includes('clinical_psychologist') || mentorProfileData.categories.includes('counselling_psychologist')) {
                                      return 'Therapist:'
                                    }
                                    if (mentorProfileData.categories.includes('doctor')) {
                                      return 'Doctor:'
                                    }
                                  }
                                  return 'Mentor:'
                                })()}
                              </strong>{' '}
                              <span>{mentorProfileData?.name}</span>
                            </div>
                          )}
                        </div>
                      </Box>
                    )}
                    <div>
                        {/* {chamberRoomsForMentorAssistantClinicLocation?.length > 0 &&
                          eventDataForSelectedEventType?.meetingSrc === 'offline' && (
                            <>
                              <div style={{ marginTop: '5px' }}>Chamber Room</div>
                              <EDVCBookNewSlotSelect
                                value={chosenChamberRoomId}
                                onChange={(e) => {
                                  setChosenChamberRoomId(e.target.value.id)
                                }}
                              >
                                {chamberRoomsForMentorAssistantClinicLocation?.map(
                                  (room, roomIndex) => (
                                    <option value={room.id} key={roomIndex}>
                                      {room?.title || room?.id}
                                    </option>
                                  ),
                                )}
                              </EDVCBookNewSlotSelect>
                            </>
                          )} */}
                        {eventDataForSelectedEventType?.type ===
                          allAvailableEventTypes.therapySession && (
                          <>
                            {relatedConsultationsLoading ? (
                              <Spinner size="sm" variant="dark" />
                            ) : (
                              <>
                                <div>Select related consultation:</div>
                                <div style={{ fontSize: '12px' }}>
                                  (Which doctor recommended therapy, and in which session?)
                                </div>
                                <select
                                  value={selectedRelatedConsultationForTherapy}
                                  style={{
                                    border: '1px solid #cecece',
                                    borderRadius: '4px',
                                  }}
                                  onChange={(e) =>
                                    setSelectedRelatedConsultationForTherapy(e.target.value)
                                  }
                                >
                                  <option value={''}>None/Other</option>
                                  {pastConsultationsOfPatient?.map(
                                    (pastConsultation, pastConsultationIdx) => {
                                      return (
                                        <option
                                          key={pastConsultationIdx}
                                          value={pastConsultation?.id}
                                        >
                                          {pastConsultation?.mentorName} (
                                          {giveTodayYesterdayElseFormattedStringPerLocal(
                                            pastConsultation?.startTime,
                                            null,
                                            true,
                                          )}
                                          )
                                        </option>
                                      )
                                    },
                                  )}
                                </select>
                              </>
                            )}
                          </>
                        )}
                        {!clinicBrandData?.bookingFormSettings?.hidePricingDetailsInBookingForm && (
                          <>
                            <div className="d-flex gap-2">
                              <div className="w-100">
                                <div style={{ marginTop: '8px', marginBottom: '6px', fontSize: '14px', color: 'rgba(67, 56, 202, 0.9)', fontWeight: 600 }}>
                                  Fee Collected <span style={{ color: 'rgba(67, 56, 202, 0.6)', fontWeight: 500, fontSize: '13px' }}>(Default: {(planDataForSelectedEventId?.currency || '')?.toUpperCase()} {planDataForSelectedEventId?.finalPrice})</span>
                                </div>
                                <div style={{ display: 'flex' }}>
                                  <EDVCBookNewSlotSelect
                                    style={{ width: '70px', borderRadius: '4px 0 0 4px' }}
                                    value={paymentDetail?.currency}
                                    onChange={(e) =>
                                      setPaymentDetail((prev) => ({
                                        ...prev,
                                        currency: e.target.value,
                                      }))
                                    }
                                  >
                                    {currency_list?.map((currencyData, currencyDataIndex) => (
                                      <option value={currencyData.code} key={currencyDataIndex}>
                                        {currencyData.code?.toUpperCase()}
                                      </option>
                                    ))}
                                  </EDVCBookNewSlotSelect>
                                  <PISectionInput
                                    style={{ borderRadius: '0 4px 4px 0' }}
                                    value={paymentDetail?.feePaid || ''}
                                    onChange={(e) =>
                                      setPaymentDetail((prev) => ({
                                        ...prev,
                                        feePaid: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </div>
                              {!hideBookButton && (
                                <div className="w-100">
                                  <>
                                    <div style={{ marginTop: '8px', marginBottom: '6px', fontSize: '14px', color: 'rgba(67, 56, 202, 0.9)', fontWeight: 600 }}>Payment Mode</div>
                                    <EDVCBookNewSlotSelect
                                      value={paymentDetail?.feePaymentMode}
                                      onChange={(e) =>
                                        setPaymentDetail((prev) => ({
                                          ...prev,
                                          feePaymentMode: e.target.value,
                                        }))
                                      }
                                    >
                                      {OFFLINE_PAYMENT_METHODS_LIST?.filter((method) => {
                                        if (method?.roleType === 'all') {
                                          return true
                                        }
                                        if (
                                          getAllRolesInBoolean().isL1TherapySupport ||
                                          getAllRolesInBoolean().isL2TherapySupport ||
                                          getAllRolesInBoolean().isL3TherapySupport
                                        ) {
                                          return method?.roleType === 'support'
                                        }
                                        if (getAllRolesInBoolean().isTeacher) {
                                          if (
                                            mentorProfileData?.categories?.includes('therapist')
                                          ) {
                                            return method?.roleType === 'therapist'
                                          }
                                          if (mentorProfileData?.categories?.includes('doctor')) {
                                            return method?.roleType === 'doctor'
                                          }
                                          return method?.roleType === 'mentor'
                                        }
                                        return false
                                      })?.map((paymentMethod, paymentMethodIndex) => (
                                        <option value={paymentMethod.id} key={paymentMethodIndex}>
                                          {paymentMethod.title}
                                        </option>
                                      ))}
                                    </EDVCBookNewSlotSelect>
                                  </>
                                </div>
                              )}
                            </div>
                            <div className="w-100">
                              {paymentDetail?.feePaymentMode !== 'cash' && (
                                <>
                                  <div style={{ marginTop: '8px', marginBottom: '6px', fontSize: '14px', color: 'rgba(67, 56, 202, 0.9)', fontWeight: 600 }}>Transaction ID</div>
                                  <PISectionInput
                                    value={paymentDetail?.feePaidTxnId}
                                    onChange={(e) =>
                                      setPaymentDetail((prev) => ({
                                        ...prev,
                                        feePaidTxnId: e.target.value,
                                      }))
                                    }
                                  />
                                </>
                              )}
                            </div>
                            <div className="w-100">
                              <div style={{ marginTop: '8px', marginBottom: '6px', fontSize: '14px', color: 'rgba(67, 56, 202, 0.9)', fontWeight: 600 }}>Note</div>
                              <TextField
                                multiline
                                minRows={1}
                                maxRows={4}
                                fullWidth
                                value={paymentDetail?.feeCollectionNote || ''}
                                onChange={(e) => {
                                  const sanitizedValue = e.target.value
                                    .replace(/<[^>]*>/g, '') // Remove HTML tags
                                    .replace(/[<>]/g, '') // Remove remaining angle brackets
                                    .slice(0, 200) // Limit to 200 characters
                                  setPaymentDetail((prev) => ({
                                    ...prev,
                                    feeCollectionNote: sanitizedValue,
                                  }))
                                }}
                                inputProps={{
                                  maxLength: 200,
                                }}
                                helperText={`${(paymentDetail?.feeCollectionNote || '').length}/200 characters`}
                                variant="outlined"
                                size="small"
                                placeholder="Add a note about this fee collection (optional)..."
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '14px',
                                    color: 'rgba(67, 56, 202, 0.9)',
                                    '& textarea': {
                                      resize: 'none',
                                      overflow: 'hidden !important',
                                      fontSize: '14px',
                                      lineHeight: 1.5,
                                    },
                                    '& fieldset': {
                                      borderWidth: '1.5px',
                                      borderColor: 'rgba(67, 56, 202, 0.25)',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: 'rgba(67, 56, 202, 0.45)',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderWidth: '2px',
                                      borderColor: 'rgba(67, 56, 202, 0.7)',
                                    },
                                  },
                                  '& .MuiFormHelperText-root': {
                                    fontSize: '12px',
                                    marginTop: '6px',
                                    color: 'rgba(67, 56, 202, 0.65)',
                                    fontWeight: 500,
                                  },
                                  '& .MuiInputBase-input::placeholder': {
                                    color: 'rgba(67, 56, 202, 0.4)',
                                    opacity: 1,
                                    fontSize: '14px',
                                  },
                                }}
                              />
                            </div>
                          </>
                        )}
                    </div>
                    {(eventDataForSelectedEventType?.type === allAvailableEventTypes.consultation ||
                      eventDataForSelectedEventType?.type ===
                        allAvailableEventTypes.therapySession) &&
                      !forMentor && (
                        <div className="d-flex flex-column w-100">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={printReceipt}
                                onChange={(e) => {
                                  setPrintReceipt(e.target.checked)
                                  sessionStorage.setItem('printReceipt', e.target.checked)
                                }}
                              />
                            }
                            label="Print Receipt for this booking"
                          />
                        </div>
                      )}
                    {eventDataForSelectedEventType?.type === allAvailableEventTypes.consultation &&
                      !forMentor && (
                        <div className="d-flex flex-column w-100">
                          <div className="d-flex align-items-center w-100">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={tryBookingScreening}
                                  onChange={(e) => {
                                    setTryBookingScreening(e.target.checked)
                                  }}
                                  icon={<CancelIcon style={{ color: 'red' }} />}
                                  checkedIcon={
                                    screenerAvailable ? (
                                      <CheckCircleIcon style={{ color: 'green' }} />
                                    ) : (
                                      <ErrorIcon sx={{ color: '#ffaa09' }} />
                                    )
                                  }
                                />
                              }
                              color="secondary"
                              label="Book with Screening"
                            />
                            <AvailableScreenerComponent
                              eventBeingBooked={{
                                apiParam: 'booked-event-info',
                                mentorId: mentorProfileData?.id,
                                userId: '',
                                startTime: dayjs
                                  .utc(clickedWantedSlotData?.startTime)
                                  .format('YYYY-MM-DD HH:mm:ss'),
                                endTime: dayjs
                                  .utc(clickedWantedSlotData?.endTime)
                                  .format('YYYY-MM-DD HH:mm:ss'),
                                eventType: eventDataForSelectedEventType?.type,
                                chamberRoomId: chosenChamberRoomId,
                                eventTypeId: eventDataForSelectedEventType?.uid,
                                subscriptionPlanId:
                                  eventDataForSelectedEventType?.subscriptionPlanId,
                                patientId: selectedPatient?.id,
                                id: '',
                                feePaid: askPayment ? paymentDetailToSubmit?.feePaid : '',
                                currency: paymentDetailToSubmit?.currency || 'inr',
                                feePaymentMode: askPayment
                                  ? paymentDetailToSubmit?.feePaymentMode
                                  : '',
                                feePaidTxnId: askPayment ? paymentDetailToSubmit?.feePaidTxnId : '',
                                clinicId: clinicId || '',
                                clinicBrandId: clinicBrandId || '',
                              }}
                              setScreenerAvailable={setScreenerAvailable}
                            />
                          </div>
                          {!tryBookingScreening && (
                            <NewBookingDialogReasonForNotDoingScreeningForm
                              reasonForNotBooking={reasonForNotDoingScreening}
                              setReasonIdForNotDoingScreening={setReasonIdForNotDoingScreening}
                              setReasonForNotBooking={setReasonForNotDoingScreening}
                              screenerAvailable={screenerAvailable}
                              reasonForNotBookingScreeningId={reasonIdForNotDoingScreening}
                            />
                          )}
                        </div>
                      )}
                  </>
                )}
              </>
            )}
            
            {/* Booking Action Buttons - Moved to bottom after payment sections */}
            {selectedPatient && !!selectedEventTypeId && (
              <Box
                sx={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '2px solid rgba(67, 56, 202, 0.12)',
                  width: '100%',
                }}
              >
                {bookingLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
                    <Spinner animation="border" variant="dark" size="md" />
                  </Box>
                ) : (
                  <BookingDialogActions
                    askPayment={askPayment}
                    paymentDetail={paymentDetailToSubmit}
                    eventDataForSelectedEventType={eventDataForSelectedEventType}
                    forMentor={forMentor}
                    tryBookingScreening={tryBookingScreening}
                    reasonForNotDoingScreening={reasonForNotDoingScreening}
                    bookNowHandler={bookNowHandler}
                    paymentLinkToSendViaQRCodeFunction={paymentLinkToSendViaQRCode}
                    clinicId={clinicId}
                    clinicBrandId={clinicBrandId}
                    mentorProfileData={mentorProfileData}
                    clickedWantedSlotData={clickedWantedSlotData}
                    selectedPatient={selectedPatient}
                    updateBookingsInCalendar={updateBookingsInCalendar}
                    setNewBookingDialogOpen={setNewBookingDialogOpen}
                    setBookingLoading={setBookingLoading}
                    setSelectedPatient={setSelectedPatient}
                    setIsNewPatient={setIsNewPatient}
                    setNewPatientInputData={setNewPatientInputData}
                    chosenChamberRoomId={chosenChamberRoomId}
                    selectedRelatedConsultationForTherapy={
                      selectedRelatedConsultationForTherapy
                    }
                    meetingBookApiSignature={meetingBookApiSignature}
                    relatedToPatient={relatedToPatient}
                    isEnoughTimeToBook={isEnoughTimeToBook}
                    forceHoldingSlot={forceHoldingSlot}
                    hideBookButton={hideBookButton}
                  />
                )}
              </Box>
            )}
          </>
        ) : (
          <div style={{ fontWeight: 700, textAlign: 'center', color: '#b73c30' }}>
            The event needs to be associated with a clinic for booking slots from here.
          </div>
        )}
      </EDVCNewBookingContainer>
    )
  }

  return (
    <>
      {isInDialog ? (
        render()
      ) : (
        <CMBCBootstrapDialog
          onClose={handleCloseDialog}
          aria-labelledby="customized-dialog-title"
          open={open}
          PaperComponent={CMBCBootStrapDialogPaperComponent}
          sx={{
            '& .MuiDialogContent-root': {
              padding: { xs: '16px', sm: '24px' },
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            },
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {Boolean(selectedPatient) && (
              <IconButton
                aria-label="reset"
                onClick={resetSelectedPatientFunction}
                onTouchStart={(e) => {
                  e.preventDefault()
                  resetSelectedPatientFunction()
                }}
              sx={{
                color: 'rgba(67, 56, 202, 0.7)',
                touchAction: 'manipulation',
                '&:hover': {
                  backgroundColor: 'rgba(67, 56, 202, 0.05)',
                  color: 'rgba(67, 56, 202, 0.9)',
                },
                '&:active': {
                  backgroundColor: 'rgba(67, 56, 202, 0.1)',
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          )}
          <IconButton
            aria-label="close"
            onClick={() => {
              setSelectedPatient(null)
              setIsNewPatient(false)
              setNewPatientInputData({
                ...BLANK_PATIENT_DATA,
                defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
              })
              handleCloseDialog()
            }}
            onTouchStart={(e) => {
              e.preventDefault()
              setSelectedPatient(null)
              setIsNewPatient(false)
              setNewPatientInputData({
                ...BLANK_PATIENT_DATA,
                defaultCommunicationLanguage: clinicBrandData?.defaultCommunicationLanguage || 'hi',
              })
              handleCloseDialog()
            }}
            sx={{
              color: 'rgba(67, 56, 202, 0.7)',
              touchAction: 'manipulation',
              '&:hover': {
                backgroundColor: 'rgba(67, 56, 202, 0.05)',
                color: 'rgba(67, 56, 202, 0.9)',
              },
              '&:active': {
                backgroundColor: 'rgba(67, 56, 202, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          </Box>

          <DialogTitle
            sx={{
              padding: '20px 24px',
              color: 'rgba(67, 56, 202, 1)',
              fontWeight: 700,
              fontSize: '18px',
              letterSpacing: '0.3px',
              borderBottom: '2px solid rgba(67, 56, 202, 0.1)',
              backgroundColor: 'rgba(67, 56, 202, 0.02)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                width: '100%',
                flexWrap: 'wrap',
                pr: { xs: '44px', sm: '52px' },
              }}
            >
              <Typography
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: '18px',
                  lineHeight: 1.3,
                  letterSpacing: '0.3px',
                  minWidth: 0,
                  flex: '1 1 220px',
                }}
              >
                {bookingDialogTitle}
              </Typography>

              {isNewPatient &&
                !selectedPatient &&
                shouldShowPatientFormUiLanguageSelector && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      minWidth: { xs: '160px', sm: '220px' },
                      maxWidth: { xs: '100%', sm: '220px' },
                      flex: '0 0 auto',
                      alignItems: { xs: 'stretch', sm: 'flex-end' },
                    }}
                  >
                    <Typography
                      component="label"
                      htmlFor="patient-form-language-select"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: 'rgba(67, 56, 202, 0.85)',
                        lineHeight: 1.2,
                      }}
                    >
                      {patientFormCopy.formLanguageLabel}
                    </Typography>
                    <TextField
                      id="patient-form-language-select"
                      select
                      size="small"
                      value={patientFormUiLanguage}
                      onChange={(event) => setPatientFormUiLanguage(event.target.value)}
                      sx={{
                        minWidth: { xs: '160px', sm: '220px' },
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                        },
                      }}
                    >
                      {patientFormI18n.uiLanguageOptions.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.id === 'en'
                            ? option.language
                            : `${option.language} (${option.localLanguageName})`}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                )}
            </Box>
          </DialogTitle>
          {render()}
        </CMBCBootstrapDialog>
      )}

      {/* Edit Session Start Dialog */}
      <Dialog
        open={editSessionStartDialogOpen}
        onClose={() => {
          setEditSessionStartDialogOpen(false)
          setSelectedCustomTime('')
          setSelectedSlotTime(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'rgba(67, 56, 202, 0.9)', fontWeight: 600 }}>Edit Session Start Time</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Available Slots as Pills */}
            {(() => {
              if (!clickedWantedSlotData?.startTime || !eventDataForSelectedEventType?.durationInMins) {
                return null
              }

              const currentDate = dayjs(clickedWantedSlotData.startTime).local()
              const duration = eventDataForSelectedEventType.durationInMins || 30
              const now = dayjs()
              const isToday = currentDate.format('YYYY-MM-DD') === now.format('YYYY-MM-DD')

              // Get timezone
              const timezone = clinicData?.schedules?.[0]?.timezone || clinicData?.timeZone?.offset || '+05:30'
              const decodedTz = typeof timezone === 'string' && timezone.includes('%') ? decodeURIComponent(timezone) : timezone

              // Generate available slots for the day (every 15 minutes from 6 AM to 11 PM)
              const availableSlots = []
              const dayStart = currentDate.startOf('day').hour(6).minute(0)
              const dayEnd = currentDate.startOf('day').hour(23).minute(0)

              for (let slotStart = dayStart; slotStart.isBefore(dayEnd); slotStart = slotStart.add(15, 'minute')) {
                // Skip past slots if today
                if (isToday && slotStart.isBefore(now.add(5, 'minute'))) {
                  continue
                }

                const slotEnd = slotStart.add(duration, 'minute')
                const slotStartUtc = slotStart.utc().toISOString()
                const slotEndUtc = slotEnd.utc().toISOString()

                // Check if slot overlaps with booked slots
                const hasConflict = bookedSlotsInDay?.some((bookedSlot) => {
                  if (!bookedSlot?.startTime || !bookedSlot?.endTime) return false
                  const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                  const bookedEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                  return slotStart.isBefore(bookedEnd) && slotEnd.isAfter(bookedStart)
                })

                if (!hasConflict) {
                  // Format time with timezone
                  let tzAbbr = 'IST'
                  try {
                    if (decodedTz === '+05:30' || decodedTz === 'Asia/Kolkata' || decodedTz === 'Asia/Calcutta') {
                      tzAbbr = 'IST'
                    } else if (decodedTz === '+05:00') {
                      tzAbbr = 'PKT'
                    } else if (decodedTz === '+08:00') {
                      tzAbbr = 'CST'
                    } else if (decodedTz === '+00:00' || decodedTz === 'UTC') {
                      tzAbbr = 'UTC'
                    } else {
                      const offsetMatch = decodedTz.match(/^([+-])(\d{2}):(\d{2})$/)
                      if (offsetMatch) {
                        const sign = offsetMatch[1]
                        const hours = parseInt(offsetMatch[2], 10)
                        const minutes = parseInt(offsetMatch[3], 10)
                        tzAbbr = minutes === 0 ? `GMT${sign}${hours}` : `GMT${sign}${hours}:${minutes}`
                      } else {
                        tzAbbr = decodedTz
                      }
                    }
                  } catch {
                    tzAbbr = 'IST'
                  }

                  availableSlots.push({
                    startTime: slotStartUtc,
                    endTime: slotEndUtc,
                    displayTime: slotStart.format('h:mm A'),
                    displayTimeWithTz: `${slotStart.format('h:mm A')} (${tzAbbr})`,
                    slotStart,
                    slotEnd,
                  })
                }
              }

              return (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                    Available Time Slots
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                    }}
                  >
                    {availableSlots.length > 0 ? (
                      availableSlots.map((slot, index) => (
                        <Chip
                          key={index}
                          label={slot.displayTime}
                          onClick={() => {
                            setSelectedSlotTime(slot)
                            setSelectedCustomTime(slot.slotStart.format('HH:mm'))
                          }}
                          color={selectedSlotTime?.startTime === slot.startTime ? 'primary' : 'default'}
                          variant={selectedSlotTime?.startTime === slot.startTime ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: selectedSlotTime?.startTime === slot.startTime ? undefined : 'rgba(103, 121, 227, 0.1)',
                            },
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No available slots found for this day
                      </Typography>
                    )}
                  </Box>
                </Box>
              )
            })()}

            {/* Custom Time Picker */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Or Select Custom Time
              </Typography>
              <TextField
                type="time"
                value={selectedCustomTime}
                onChange={(e) => {
                  setSelectedCustomTime(e.target.value)
                  setSelectedSlotTime(null)
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 900, // 15 min intervals
                }}
                fullWidth
                sx={{ maxWidth: 200 }}
              />
              {selectedCustomTime && (() => {
                if (!clickedWantedSlotData?.startTime || !eventDataForSelectedEventType?.durationInMins) {
                  return null
                }

                const [hours, minutes] = selectedCustomTime.split(':')
                const currentDate = dayjs(clickedWantedSlotData.startTime).local()
                const customSlotStart = currentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
                const customSlotEnd = customSlotStart.add(eventDataForSelectedEventType.durationInMins, 'minute')

                // Check for conflicts
                const hasConflict = bookedSlotsInDay?.some((bookedSlot) => {
                  if (!bookedSlot?.startTime || !bookedSlot?.endTime) return false
                  const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                  const bookedEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                  return customSlotStart.isBefore(bookedEnd) && customSlotEnd.isAfter(bookedStart)
                })

                return (
                  <Box sx={{ mt: 2 }}>
                    {hasConflict ? (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        This time slot is already booked. Please select a different time.
                      </Alert>
                    ) : (
                      <Alert severity="success" sx={{ mt: 1 }}>
                        Time slot is available: {customSlotStart.format('DD/MM/YYYY, h:mm A')} - {customSlotEnd.format('h:mm A')}
                      </Alert>
                    )}
                  </Box>
                )
              })()}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => {
              setEditSessionStartDialogOpen(false)
              setSelectedCustomTime('')
              setSelectedSlotTime(null)
            }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            variant="contained"
            onClick={() => {
              let newSlotData = null

              if (selectedSlotTime) {
                // Use selected slot from pills
                newSlotData = {
                  startTime: selectedSlotTime.startTime,
                  endTime: selectedSlotTime.endTime,
                }
              } else if (selectedCustomTime) {
                // Use custom time if no conflict
                const [hours, minutes] = selectedCustomTime.split(':')
                const currentDate = dayjs(clickedWantedSlotData.startTime).local()
                const customSlotStart = currentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
                const customSlotEnd = customSlotStart.add(eventDataForSelectedEventType?.durationInMins || 30, 'minute')

                // Double-check for conflicts
                const hasConflict = bookedSlotsInDay?.some((bookedSlot) => {
                  if (!bookedSlot?.startTime || !bookedSlot?.endTime) return false
                  const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                  const bookedEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                  return customSlotStart.isBefore(bookedEnd) && customSlotEnd.isAfter(bookedStart)
                })

                if (!hasConflict) {
                  const timezone = clinicData?.schedules?.[0]?.timezone || clinicData?.timeZone?.offset || '+05:30'
                  const decodedTz = typeof timezone === 'string' && timezone.includes('%') ? decodeURIComponent(timezone) : timezone
                  
                  newSlotData = {
                    startTime: customSlotStart.utc().format(`YYYY-MM-DDTHH:mm:ss[${decodedTz}]`),
                    endTime: customSlotEnd.utc().format(`YYYY-MM-DDTHH:mm:ss[${decodedTz}]`),
                  }
                }
              }

              if (newSlotData) {
                setClickedWantedSlotData(newSlotData)
                setEditSessionStartDialogOpen(false)
                setSelectedCustomTime('')
                setSelectedSlotTime(null)
                toast.success('Session start time updated')
              } else {
                toast.error('Please select a valid time slot')
              }
            }}
            disabled={(() => {
              if (selectedSlotTime) return false
              if (!selectedCustomTime) return true
              
              // Check if custom time has conflict
              if (!clickedWantedSlotData?.startTime || !eventDataForSelectedEventType?.durationInMins) {
                return true
              }

              const [hours, minutes] = selectedCustomTime.split(':')
              const currentDate = dayjs(clickedWantedSlotData.startTime).local()
              const customSlotStart = currentDate.hour(parseInt(hours)).minute(parseInt(minutes)).second(0)
              const customSlotEnd = customSlotStart.add(eventDataForSelectedEventType.durationInMins, 'minute')

              const hasConflict = bookedSlotsInDay?.some((bookedSlot) => {
                if (!bookedSlot?.startTime || !bookedSlot?.endTime) return false
                const bookedStart = dayjs(bookedSlot?.startTimeForDayCal || bookedSlot?.startTime)
                const bookedEnd = dayjs(bookedSlot?.endTimeForDayCal || bookedSlot?.endTime)
                return customSlotStart.isBefore(bookedEnd) && customSlotEnd.isAfter(bookedStart)
              })

              return hasConflict
            })()}
          >
            Save
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
